import FullLayout from 'layout/FullLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL, get, put } from 'lib/network/api';
import { format } from 'date-fns';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InputText from 'components/InputText';
import InputSelected from 'components/InputSelected';

const isInvalidDate = (dateString) => {
  return !dateString || typeof dateString !== 'string' || dateString.startsWith('0001-01-01');
};

const fetchUserProfile = async () => {
  const response = await get(`${API_URL}/api/user`);
  if (!response || response.error) {
    throw new Error(response?.message || 'Failed to fetch user data');
  }
  return response;
};

const Profile = () => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const genderOptions = [
    { id: 1, name: 'Male', value: 'male' },
    { id: 2, name: 'Female', value: 'female' },
  ];

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    dob: Yup.date().nullable(),
    gender: Yup.string(),
  });

  const accountValidationSchema = Yup.object({
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone_number: Yup.string().required('Phone number is required'),
  });

  const passwordValidationSchema = Yup.object({
    current_password: Yup.string().required('Current password is required'),
    new_password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('New password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .required('Please confirm your password'),
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['user-profile'],
    queryFn: fetchUserProfile,
    staleTime: 1000 * 60 * 5, // cache 5 menit
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (formData) => {
      return put(`${API_URL}/api/user`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-profile']);
      setIsEditing(false);
      setIsEditingAccount(false);
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update profile');
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: async (passwordData) => {
      return put(`${API_URL}/api/user/change-password`, passwordData);
    },
    onSuccess: () => {
      setIsEditingPassword(false);
      toast.success('Password updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update password');
    },
  });

  const handleSubmitProfile = (values, { setSubmitting }) => {
    const payload = {
      email: values.email,
      phone_number: values.phone_number,
      address: values.address,
      date_of_birth: values.dob,
      gender: values.gender,
    };
    updateProfileMutation.mutate(payload);
    setSubmitting(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleEditAccountClick = () => {
    setIsEditingAccount(true);
  };

  const handleCancelAccountEdit = () => {
    setIsEditingAccount(false);
  };

  const handleEditPasswordClick = () => {
    setIsEditingPassword(true);
  };

  const handleCancelPasswordEdit = () => {
    setIsEditingPassword(false);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto pt-6 pb-24 text-kin-text">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto pt-6 pb-24 text-kin-text">
        <p className="text-red-500">Error: {error.message}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-6">
        <h2 className="text-xl font-semibold">My Profile</h2>

        {/* Account Information */}
        <div className="bg-white rounded-xl py-5 px-3 sm:px-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
            <span className="text-base font-semibold">Account Information</span>
            {!isEditingAccount ? (
              <button
                onClick={handleEditAccountClick}
                className="border border-primary-main text-primary-main rounded-[100px] justify-center items-center py-2 px-4 mt-2 sm:mt-0">
                <span className="text-xs font-semibold block font-regular">EDIT</span>
              </button>
            ) : (
              <div className="flex gap-2 mt-2 sm:mt-0">
                <button
                  onClick={handleCancelAccountEdit}
                  className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                  <span className="text-xs font-semibold block">CANCEL</span>
                </button>
              </div>
            )}
          </div>

          {!isEditingAccount ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 mt-4">
              <div className="grid grid-cols-1 gap-y-6 col-span-1">
                <div className="flex flex-col gap-1">
                  <h5 className="font-light text-sm leading-[normal]">EMAIL ADDRESS</h5>
                  <h5 className="font-regular text-sm leading-[normal]">{data?.email || 'N/A'}</h5>
                </div>

                <div className="flex flex-col gap-1">
                  <h5 className="font-light text-sm leading-[normal]">MOBILE NUMBER</h5>
                  <h5 className="font-regular text-sm leading-[normal]">
                    {data?.phone_number || 'N/A'}
                  </h5>
                </div>
              </div>
            </div>
          ) : (
            <Formik
              initialValues={{
                email: data?.email || '',
                phone_number: data?.phone_number || '',
                name: data?.name || '',
                dob: (() => {
                  if (!data?.date_of_birth || isInvalidDate(data?.date_of_birth)) {
                    return '';
                  }
                  const dateString = data.date_of_birth.toString();
                  const datePart = dateString.includes(' ')
                    ? dateString.split(' ')[0]
                    : dateString.split('T')[0];

                  return datePart.match(/^\d{4}-\d{2}-\d{2}$/) ? datePart : '';
                })(),
                gender: data?.gender || '',
              }}
              validationSchema={accountValidationSchema}
              onSubmit={handleSubmitProfile}>
              {() => (
                <Form className="flex flex-col gap-4 mt-4">
                  <InputText
                    name="email"
                    type="email"
                    label="EMAIL ADDRESS"
                    placeholder="Enter your email"
                    required
                  />

                  <InputText
                    name="phone_number"
                    type="tel"
                    label="MOBILE NUMBER"
                    placeholder="Enter your phone number"
                    required
                  />

                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 mt-4">
                    <button
                      type="button"
                      onClick={handleCancelAccountEdit}
                      className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                      <span className="text-xs font-semibold block">CANCEL</span>
                    </button>
                    <button
                      type="submit"
                      disabled={updateProfileMutation.isPending}
                      className="bg-primary-main text-white rounded-[100px] justify-center items-center py-2 px-4 disabled:opacity-50">
                      <span className="text-xs font-semibold block">
                        {updateProfileMutation.isPending ? 'SAVING...' : 'SAVE'}
                      </span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>

        {/* Password Section */}
        <div className="bg-white rounded-xl py-5 px-3 sm:px-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
            <span className="text-base font-semibold">Password</span>
            {!isEditingPassword ? (
              <button
                onClick={handleEditPasswordClick}
                className="border border-primary-main text-primary-main rounded-[100px] justify-center items-center py-2 px-4 mt-2 sm:mt-0">
                <span className="text-xs font-semibold block font-regular">CHANGE PASSWORD</span>
              </button>
            ) : (
              <div className="flex gap-2 mt-2 sm:mt-0">
                <button
                  onClick={handleCancelPasswordEdit}
                  className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                  <span className="text-xs font-semibold block">CANCEL</span>
                </button>
              </div>
            )}
          </div>

          {!isEditingPassword ? (
            <div className="mt-4">
              <div className="flex flex-col gap-1">
                <h5 className="font-light text-sm leading-[normal]">PASSWORD</h5>
                <h5 className="font-regular text-sm leading-[normal]">*******</h5>
              </div>
            </div>
          ) : (
            <Formik
              initialValues={{
                current_password: '',
                new_password: '',
                confirm_password: '',
              }}
              validationSchema={passwordValidationSchema}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                const payload = {
                  current_password: values.current_password,
                  new_password: values.new_password,
                  confirm_password: values.confirm_password,
                };
                updatePasswordMutation.mutate(payload);
                setSubmitting(false);
                resetForm();
              }}>
              {() => (
                <Form className="flex flex-col gap-4 mt-4">
                  <InputText
                    name="current_password"
                    type="password"
                    label="CURRENT PASSWORD"
                    placeholder="Enter your current password"
                    required
                  />

                  <InputText
                    name="new_password"
                    type="password"
                    label="NEW PASSWORD"
                    placeholder="Enter new password"
                    required
                  />

                  <InputText
                    name="confirm_password"
                    type="password"
                    label="CONFIRM NEW PASSWORD"
                    placeholder="Confirm your new password"
                    required
                  />

                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 mt-4">
                    <button
                      type="button"
                      onClick={handleCancelPasswordEdit}
                      className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                      <span className="text-xs font-semibold block">CANCEL</span>
                    </button>
                    <button
                      type="submit"
                      disabled={updatePasswordMutation.isPending}
                      className="bg-primary-main text-white rounded-[100px] justify-center items-center py-2 px-4 disabled:opacity-50">
                      <span className="text-xs font-semibold block">
                        {updateProfileMutation.isPending ? 'UPDATING...' : 'UPDATE PASSWORD'}
                      </span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-xl pt-5 px-3 sm:px-8 pb-7">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
            <span className="text-base font-semibold">Personal Information</span>
            {!isEditing ? (
              <button
                onClick={handleEditClick}
                className="border border-primary-main text-primary-main rounded-[100px] justify-center items-center py-2 px-4 mt-2 sm:mt-0">
                <span className="text-xs font-semibold block font-regular">EDIT</span>
              </button>
            ) : (
              <div className="flex gap-2 mt-2 sm:mt-0">
                <button
                  onClick={handleCancelEdit}
                  className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                  <span className="text-xs font-semibold block">CANCEL</span>
                </button>
              </div>
            )}
          </div>

          {!isEditing ? (
            <div className="flex flex-col gap-4 mt-4">
              <div className="flex flex-col gap-1">
                <h5 className="font-light text-sm leading-[normal]">NAME</h5>
                <h5 className="font-regular text-sm leading-[normal]">{data?.name || 'N/A'}</h5>
              </div>

              <div className="flex flex-col gap-1">
                <h5 className="font-light text-sm leading-[normal]">DATE OF BIRTH</h5>
                <h5 className="font-regular text-sm leading-[normal]">
                  {isInvalidDate(data?.date_of_birth)
                    ? 'N/A'
                    : format(data?.date_of_birth, 'dd MMM yyyy')}
                </h5>
              </div>

              <div className="flex flex-col gap-1">
                <h5 className="font-light text-sm leading-[normal]">GENDER</h5>
                <h5 className="font-regular text-sm leading-[normal]">{data?.gender || 'N/A'}</h5>
              </div>
            </div>
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                email: data?.email || '',
                phone_number: data?.phone_number || '',
                name: data?.name || '',
                dob: (() => {
                  if (!data?.date_of_birth || isInvalidDate(data?.date_of_birth)) {
                    return '';
                  }
                  const dateString = data.date_of_birth.toString();
                  const datePart = dateString.includes(' ')
                    ? dateString.split(' ')[0]
                    : dateString.split('T')[0];

                  return datePart.match(/^\d{4}-\d{2}-\d{2}$/) ? datePart : '';
                })(),
                gender: data?.gender || '',
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmitProfile}>
              {({ values, setFieldValue }) => (
                <Form className="flex flex-col gap-4 mt-4">
                  <InputText
                    name="name"
                    type="text"
                    label="NAME"
                    placeholder="Enter your name"
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium leading-6 text-kin-text">
                      DATE OF BIRTH
                    </label>
                    <input
                      name="dob"
                      type="date"
                      value={values.dob}
                      onChange={(e) => setFieldValue('dob', e.target.value)}
                      className="mt-1 block w-full rounded-xl border-0 py-1.5 shadow-sm text-kin-text font-regular ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-main text-sm"
                    />
                  </div>

                  <InputSelected
                    dataOption={genderOptions}
                    selected={genderOptions.find((option) => option.value === values.gender)}
                    setSelected={(selectedGender) =>
                      setFieldValue('gender', selectedGender ? selectedGender.value : '')
                    }
                    label="GENDER"
                    placeholder="Select gender"
                  />

                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 mt-4">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="border border-gray-300 text-gray-600 rounded-[100px] justify-center items-center py-2 px-4">
                      <span className="text-xs font-semibold block">CANCEL</span>
                    </button>
                    <button
                      type="submit"
                      disabled={updateProfileMutation.isPending}
                      className="bg-primary-main text-white rounded-[100px] justify-center items-center py-2 px-4 disabled:opacity-50">
                      <span className="text-xs font-semibold block">
                        {updateProfileMutation.isPending ? 'SAVING...' : 'SAVE'}
                      </span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
